/**
 * A live voice-typing session through the browser's standard speech service (ADR-002 Part III §24.2–24.4, §26, §31).
 *
 * This is the ONLY file in apps/web that constructs the recogniser (I1). It sets `continuous`, `interimResults` and
 * `lang`, and calls `start()` with NO argument, so the live microphone is the only input (I3). It never sets
 * `processLocally` and never calls `available()` or `install()` (I2). It holds the caller's time budget across its
 * own restarts (I4: Meetings passes what is left of 120 s, so "Record more" draws on the same 2:00).
 *
 * Behaviour the screens rely on:
 *  - Final words go to `onFinal` (the caller appends them at the END of its text, so earlier edits are never
 *    overwritten). The phrase still being heard goes to `onInterim`.
 *  - An unrequested `end` (the service ends on silence) restarts quietly while time is left. Three restarts within
 *    10 s stop it with `restarts`.
 *  - Unbroken speech: the service may never finalise while someone keeps talking, so nothing would reach the editor
 *    and the phrase would only grow. A phrase pending for LONG_PHRASE_MS is committed by cycling the segment
 *    (`stop()` finalises it, or the salvage below keeps it) and restarting at once. These cycles are ours, so they
 *    never count toward the restart limit.
 *  - The last interim phrase is kept and handed over as final when a segment ends without finalising it (the
 *    salvage the spike found necessary).
 *  - Every error is a stop with the text kept: `blocked` (not-allowed, audio-capture, service-not-allowed),
 *    `network`, or `error`. Nothing recognised in the first 20 s stops it with `nothing`.
 *  - `onEnd` is called exactly once.
 */
import { recogniserCtor } from './probe';

export type StopReason =
  /** The person pressed Stop (or left, and chose Stop). */
  | 'stopped'
  /** The time budget ran out. */
  | 'cap'
  /** The microphone is blocked or missing, or the speech service is not allowed (e.g. Brave). */
  | 'blocked'
  /** The speech service needs the internet and lost it. */
  | 'network'
  /** Nothing was recognised in the first 20 s. */
  | 'nothing'
  /** The service kept ending: three restarts within 10 s. */
  | 'restarts'
  /** Any other failure (language not supported, the service refused). */
  | 'error';

export interface LiveHandlers {
  /** The microphone is open and the service is listening. */
  onStart?: () => void;
  /** Recognised words, final. Append them at the end of the text. */
  onFinal: (text: string) => void;
  /** The phrase still being heard ('' when there is none). */
  onInterim: (text: string) => void;
  /** Milliseconds of listening so far in this session (about 4 times a second). */
  onTick?: (usedMs: number) => void;
  /** Called once: why it stopped and how long it listened (never more than the budget). */
  onEnd: (reason: StopReason, usedMs: number) => void;
}

export interface LiveSession {
  /** Stop listening; the last words still come through `onFinal`, then `onEnd('stopped')`. */
  stop: () => void;
}

/** The parts of the Web Speech recogniser this module uses (TypeScript's DOM library does not declare it). */
interface Recogniser {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onresult: ((e: RecognitionEvent) => void) | null;
}
interface RecognitionEvent {
  resultIndex: number;
  results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>;
}

export const NOTHING_HEARD_MS = 20_000;
const RESTART_WINDOW_MS = 10_000;
const RESTARTS_IN_WINDOW = 3;
const TICK_MS = 250;
/** A phrase still pending after this long is committed (unbroken speech never pauses for the service to finalise). */
export const LONG_PHRASE_MS = 6_000;

/** Join a new piece onto existing text with one space, never doubling one. */
export function appendWords(text: string, piece: string): string {
  const p = piece.trim();
  if (!p) return text;
  if (!text) return p;
  return /\s$/.test(text) ? text + p : `${text} ${p}`;
}

/**
 * Start listening. `budgetMs` is the time left for this meeting or note; at most that is listened to.
 * Throws if the browser has no recogniser (call `probe()` first).
 */
export function startLive(opts: { budgetMs: number; lang?: string } & LiveHandlers): LiveSession {
  const Ctor = recogniserCtor();
  if (!Ctor) throw new Error('Voice typing is not available in this browser.');
  const rec = new Ctor() as Recogniser;
  rec.continuous = true;
  rec.interimResults = true;
  rec.lang = opts.lang ?? 'en-IN';
  rec.maxAlternatives = 1;

  const budget = Math.max(0, opts.budgetMs);
  let used = 0;
  let activeSince: number | null = null;
  let heardAny = false;
  let interim = '';
  let finalised = 0;
  let wanted: StopReason | null = null;
  let ended = false;
  let restarts: number[] = [];
  let fallback: number | null = null;
  /** When the current interim phrase started (null when none is pending). */
  let pendingSince: number | null = null;
  /** We stopped the segment to commit a long phrase; its `end` restarts without counting as unrequested. */
  let cycling = false;
  const startedAt = performance.now();

  const usedNow = () => Math.min(budget, used + (activeSince === null ? 0 : performance.now() - activeSince));

  const finish = (reason: StopReason) => {
    if (ended) return;
    ended = true;
    window.clearInterval(ticker);
    if (fallback !== null) window.clearTimeout(fallback);
    if (activeSince !== null) used += performance.now() - activeSince;
    activeSince = null;
    if (interim.trim()) opts.onFinal(interim);
    interim = '';
    opts.onInterim('');
    rec.onstart = rec.onend = rec.onerror = rec.onresult = null;
    try {
      rec.abort();
    } catch {
      /* already stopped */
    }
    opts.onEnd(reason, Math.round(Math.min(budget, used)));
  };

  /** Ask the service to stop; the final words arrive, then `end` finishes. If `end` never comes, finish anyway. */
  const requestStop = (reason: StopReason) => {
    if (ended || wanted) return;
    wanted = reason;
    try {
      rec.stop();
    } catch {
      finish(reason);
      return;
    }
    fallback = window.setTimeout(() => finish(reason), 1500);
  };

  rec.onstart = () => {
    if (activeSince === null) activeSince = performance.now();
    opts.onStart?.();
  };

  rec.onresult = (e) => {
    heardAny = true;
    let pending = '';
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const r = e.results[i]!;
      const t = r[0].transcript;
      if (r.isFinal) {
        if (i >= finalised) {
          opts.onFinal(t);
          finalised = i + 1;
        }
      } else pending = appendWords(pending, t);
    }
    interim = pending;
    pendingSince = interim ? (pendingSince ?? performance.now()) : null;
    opts.onInterim(interim);
  };

  rec.onerror = (e) => {
    switch (e.error) {
      case 'no-speech':
      case 'aborted':
        return; // silence ends the segment; `end` restarts it. `aborted` is our own stop.
      case 'not-allowed':
      case 'audio-capture':
      case 'service-not-allowed':
        wanted = wanted ?? 'blocked';
        return;
      case 'network':
        wanted = wanted ?? 'network';
        return;
      default:
        wanted = wanted ?? 'error';
    }
  };

  rec.onend = () => {
    if (activeSince !== null) used += performance.now() - activeSince;
    activeSince = null;
    // The segment ended without finalising its last phrase: keep it.
    if (interim.trim()) {
      opts.onFinal(interim);
      interim = '';
      opts.onInterim('');
    }
    finalised = 0;
    pendingSince = null;
    const ours = cycling;
    cycling = false;
    if (wanted) return finish(wanted);
    if (used >= budget) return finish('cap');
    // An unrequested end (silence): restart quietly, unless it keeps ending. Our own long-phrase cycle just restarts.
    if (!ours) {
      const now = performance.now();
      restarts = [...restarts.filter((t) => now - t < RESTART_WINDOW_MS), now];
      if (restarts.length >= RESTARTS_IN_WINDOW) return finish('restarts');
    }
    try {
      rec.start();
    } catch {
      finish('error');
    }
  };

  const ticker = window.setInterval(() => {
    if (ended) return;
    const u = usedNow();
    opts.onTick?.(u);
    if (u >= budget) requestStop('cap');
    else if (!heardAny && performance.now() - startedAt >= NOTHING_HEARD_MS) requestStop('nothing');
    else if (!wanted && !cycling && pendingSince !== null && performance.now() - pendingSince >= LONG_PHRASE_MS) {
      cycling = true;
      try {
        rec.stop();
      } catch {
        cycling = false;
      }
    }
  }, TICK_MS);

  try {
    rec.start();
  } catch {
    window.setTimeout(() => finish('error'), 0);
  }

  return { stop: () => requestStop('stopped') };
}
